<script setup lang="ts">
import { ref, computed, watch, inject, onMounted, onUnmounted } from 'vue';
import { isNoE } from '@/utils/obj-utils';
import { FormTextareaEmits, FormTextareaProps, FormTextareaRef } from '@/types/form/form-textarea';
import { DynamicFormContext } from '@/types/form/dynamic-form';
import utils from '@/utils';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';

const props = defineProps<FormTextareaProps>();
const emit = defineEmits<FormTextareaEmits>();
const FIELD_TYPE = 'form-textarea';
const formContext = inject<DynamicFormContext>('formContext')!;
const textareaRef = ref<HTMLTextAreaElement>();
const value = ref('');
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const focused = ref(false);
const msg = ref<FormElMsgData>();

const fields = computed(() => props.item?.fields);

const hasValue = computed(() => !utils.obj.isNoE(value.value));
const label = computed(() => fields.value?.label?.value || '');
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(() => fields.value?.fieldName?.value || '');

const onMainClick = () => {
  textareaRef.value!.focus();
};
const onFocus = () => {
  focused.value = true;
};
const onBlur = () => {
  focused.value = false;
};
const onInput = (e: InputEvent) => {
  const el = e.target as HTMLTextAreaElement | undefined;
  value.value = el?.value ?? '';
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, value.value, value.value);
};

const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue: string) => {
  value.value = newValue || '';
};
const getValue = () => {
  return value.value;
};

const validate = async () => {
  const result = {
    value: value.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value) {
    return result;
  }

  if (isRequired.value && isNoE(value.value)) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  msg.value = undefined;
  return result;
};

const getRefData = (): FormTextareaRef => ({
  el: textareaRef.value!,
  type: 'form-textarea',
  name: fieldName.value,
  id: props.item.id,
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  getValue,
  validate,
});

// Register this component with parent
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
});
onUnmounted(() => {
  formContext.unRegFormEl(getRefData());
});
defineExpose(() => getRefData());
</script>
<template>
  <div
    :class="['f-textarea', { disabled: isDisabled, 'has-error': msg?.type === 'error' }]"
    v-if="isVisible"
  >
    <div class="f-textarea__main" @click="onMainClick">
      <div class="f-textarea__placeholder" :class="[{ 'as-label': focused || hasValue }]">
        <span
          class="f-textarea__label"
          v-html="focused || hasValue ? label : utils.string.ifEmpty(placeholder, label)"
        />
        <span class="f-textarea__required" v-if="isRequired">*</span>
      </div>
      <div class="f-textarea__text">
        <pre>{{ utils.string.ifEmpty(value, placeholder) }}</pre>
        <textarea
          :value="value"
          @focus="onFocus"
          @blur="onBlur"
          @input="onInput"
          :disabled="isDisabled"
          ref="textareaRef"
        />
      </div>
    </div>
    <form-el-msg :msg="msg" />
  </div>
</template>
<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.f-textarea {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--item-color, #000000);
  &__placeholder {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    color: var(--item-color, #929292);
    padding: 10px 16px;
    transition: all 0.2s ease-in-out;
    &.as-label {
      font-size: 12px;
      height: 36px;
      opacity: 0.7;
    }
  }
  &__main {
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 12px;
    padding: 36px 16px 8px 16px;
    border-radius: 4px;
    border: 1px solid var(--item-border-color, #c4c4c4);
    background: var(--item-back-color, #ffffff);
    &:focus-within {
      box-shadow: 0 0 0 2px rgba(8, 49, 194, 0.1);
    }
  }

  &__text {
    position: relative;
    width: 100%;
    pre {
      display: block;
      min-height: 80px;
      margin: 0;
      padding: 0;
      font-family: 'Figtree', sans-serif;
      font-size: 14px;
      line-height: 1.3;
      white-space: pre-wrap;
      word-wrap: break-word;
      visibility: hidden;
    }
    textarea {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      resize: none;
      overflow: auto;
      border: none;
      color: $black;
      background: transparent;
      font-family: 'Figtree', sans-serif;
      font-size: 16px;
      outline: none;
      line-height: 1.3;
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  &.disabled {
    .f-textarea__text {
      background-color: rgba($grey, 0.3);
      cursor: not-allowed;

      textarea {
        cursor: not-allowed;
      }
    }
  }

  &.has-error {
    .f-textarea__text {
      border-color: #dc3545;
    }
  }

  &__error {
    font-size: 12px;
    color: #dc3545;
    margin-top: 4px;
  }
}
</style>
